// DLC_09 · e-Doręczenia — hooki TanStack Query.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "./api";

const qk = {
  root: ["edoreczenia"] as const,
  summary: ["edoreczenia", "summary"] as const,
  outbox: (p?: Record<string, string>) => ["edoreczenia", "outbox", p ?? {}] as const,
  inbox: (p?: Record<string, string>) => ["edoreczenia", "inbox", p ?? {}] as const,
  mailboxes: ["edoreczenia", "mailboxes"] as const,
  recipients: (q: string) => ["edoreczenia", "recipients", q] as const,
};

export const useEdoreczeniaSummary = (enabled = true) =>
  useQuery({ queryKey: qk.summary, queryFn: api.getSummary, enabled, staleTime: 30_000 });

export const useOutbox = (params?: Record<string, string>) =>
  useQuery({ queryKey: qk.outbox(params), queryFn: () => api.getOutbox(params) });

export const useInbox = (params?: Record<string, string>, enabled = true) =>
  useQuery({ queryKey: qk.inbox(params), queryFn: () => api.getInbox(params), enabled });

export const useMailboxOptions = () =>
  useQuery({
    queryKey: ["edoreczenia", "mailbox-options"] as const,
    queryFn: api.getMailboxOptions,
    staleTime: 300_000,
  });

export const useMailboxes = (enabled = true) =>
  useQuery({ queryKey: qk.mailboxes, queryFn: api.getMailboxes, enabled });

export const useRecipients = (query: string) =>
  useQuery({
    queryKey: qk.recipients(query),
    queryFn: () => api.getRecipients(query),
  });

// Wszystkie mutacje unieważniają cały moduł: liczniki w hubie i podsumowanie muszą się
// zgadzać po każdej akcji (akceptacja zmienia kolejkę, wysyłka — dziennik i dowody).
export const useOutboxMutations = () => {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: qk.root });
  return {
    queue: useMutation({ mutationFn: (fd: FormData) => api.queueMessage(fd), onSuccess: inv }),
    update: useMutation({
      mutationFn: (v: { id: string; fd: FormData }) => api.updateQueuedMessage(v.id, v.fd),
      onSuccess: inv,
    }),
    approve: useMutation({ mutationFn: (id: string) => api.approveMessage(id), onSuccess: inv }),
    reject: useMutation({
      mutationFn: (v: { id: string; reason: string }) => api.rejectMessage(v.id, v.reason),
      onSuccess: inv,
    }),
    cancel: useMutation({ mutationFn: (id: string) => api.cancelMessage(id), onSuccess: inv }),
    send: useMutation({ mutationFn: (id: string) => api.sendMessage(id), onSuccess: inv }),
    refresh: useMutation({ mutationFn: (id: string) => api.refreshMessage(id), onSuccess: inv }),
  };
};

export const useInboxMutations = () => {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: qk.root });
  return {
    sync: useMutation({ mutationFn: () => api.syncInbox(), onSuccess: inv }),
    fetch: useMutation({ mutationFn: (id: string) => api.fetchIncoming(id), onSuccess: inv }),
    file: useMutation({
      mutationFn: (v: {
        id: string;
        target_users?: string[];
        target_project?: string | null;
        note?: string;
      }) => api.fileIncomingToRegister(v.id, v),
      onSuccess: inv,
    }),
    handleExternally: useMutation({
      mutationFn: (v: { id: string; note: string }) => api.handleIncomingExternally(v.id, v.note),
      onSuccess: inv,
    }),
  };
};

export const useMailboxMutations = () => {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: qk.root });
  return {
    create: useMutation({
      mutationFn: (payload: Record<string, unknown>) => api.createMailbox(payload),
      onSuccess: inv,
    }),
    update: useMutation({
      mutationFn: (v: { id: string; payload: Record<string, unknown> }) =>
        api.updateMailbox(v.id, v.payload),
      onSuccess: inv,
    }),
    testConnection: useMutation({
      mutationFn: (id: string) => api.testMailboxConnection(id),
      onSuccess: inv,
    }),
    uploadCertificate: useMutation({
      mutationFn: (v: { id: string; file: File }) => api.uploadMailboxCertificate(v.id, v.file),
      onSuccess: inv,
    }),
  };
};
